// covered-by: frontends/carrie/src/routes/ask-puntt/__tests__/askPunttPaths.test.ts
//
// Single source of truth for Ask Puntt path-based deep links. Resource identity
// lives in path segments; ephemeral refinements (version pin) stay in search.

import {
  type CampaignSearch,
  type CampaignSearchInput,
  campaignSearch,
  parseCampaignSearch,
} from "./campaignSearch";
import {
  type ChatSearch,
  type ChatSearchInput,
  chatSearch,
} from "./chatSearch";

export const ASK_PUNTT_PROJECT_PATH = "/ask-puntt/projects/$projectId";
/** @deprecated Use ASK_PUNTT_PROJECT_PATH */
export const ASK_PUNTT_CAMPAIGN_PATH = ASK_PUNTT_PROJECT_PATH;
export const ASK_PUNTT_PROJECT_CHAT_PATH =
  "/ask-puntt/projects/$projectId/chats/$chatId";
export const ASK_PUNTT_PROJECT_REVIEW_PATH =
  "/ask-puntt/projects/$projectId/reviews/$ticketId";
export const ASK_PUNTT_PROJECT_REVIEW_COMMENT_PATH =
  "/ask-puntt/projects/$projectId/reviews/$ticketId/comments/$commentId";
export const ASK_PUNTT_CHAT_PATH = "/ask-puntt/$chatId";
export const ASK_PUNTT_CHAT_REVIEW_PATH =
  "/ask-puntt/$chatId/reviews/$ticketId";
export const ASK_PUNTT_CHAT_REVIEW_COMMENT_PATH =
  "/ask-puntt/$chatId/reviews/$ticketId/comments/$commentId";
export const ASK_PUNTT_CHAT_CANVAS_PATH =
  "/ask-puntt/$chatId/canvas/$messageId";

export type AskPunttReviewSearch = {
  version?: number;
};

/** Search allowed on path-based project review routes (version pin + personal chat context). */
export type AskPunttProjectReviewRouteSearch = AskPunttReviewSearch &
  Pick<CampaignSearch, "tab" | "chatId">;

export function parseProjectReviewRouteSearch(
  search: Record<string, unknown>,
): AskPunttProjectReviewRouteSearch {
  const parsed = parseCampaignSearch(search);
  const out: AskPunttProjectReviewRouteSearch = {};
  if (parsed.version != null) out.version = parsed.version;
  if (parsed.chatId) {
    out.chatId = parsed.chatId;
    out.tab = parsed.tab ?? "chats";
  }
  return out;
}

export type AskPunttLocation = {
  projectId?: string;
  chatId?: string;
  ticketId?: string;
  commentId?: string;
  canvasMessageId?: string;
  version?: number;
};

function reviewSearch(version?: number): AskPunttReviewSearch {
  return version != null && version >= 0 ? { version } : {};
}

export function buildProjectReviewPath(
  projectId: string,
  ticketId: string,
  commentId?: string,
  version?: number,
) {
  if (commentId) {
    return {
      to: ASK_PUNTT_PROJECT_REVIEW_COMMENT_PATH,
      params: { projectId, ticketId, commentId },
      search: reviewSearch(version),
    } as const;
  }
  return {
    to: ASK_PUNTT_PROJECT_REVIEW_PATH,
    params: { projectId, ticketId },
    search: reviewSearch(version),
  } as const;
}

export function buildChatReviewPath(
  chatId: string,
  ticketId: string,
  commentId?: string,
  version?: number,
) {
  if (commentId) {
    return {
      to: ASK_PUNTT_CHAT_REVIEW_COMMENT_PATH,
      params: { chatId, ticketId, commentId },
      search: reviewSearch(version),
    } as const;
  }
  return {
    to: ASK_PUNTT_CHAT_REVIEW_PATH,
    params: { chatId, ticketId },
    search: reviewSearch(version),
  } as const;
}

export function buildProjectChatPath(projectId: string, chatId: string) {
  return {
    to: ASK_PUNTT_PROJECT_CHAT_PATH,
    params: { projectId, chatId },
    search: {},
  } as const;
}

export function buildProjectFeedPath(
  projectId: string,
  search?: CampaignSearchInput,
) {
  return {
    to: ASK_PUNTT_PROJECT_PATH,
    params: { projectId },
    search: campaignSearch(search),
  } as const;
}

export function buildChatPath(chatId: string, search?: ChatSearchInput) {
  return {
    to: ASK_PUNTT_CHAT_PATH,
    params: { chatId },
    search: chatSearch(search),
  } as const;
}

export function buildChatCanvasPath(chatId: string, messageId: string) {
  return {
    to: ASK_PUNTT_CHAT_CANVAS_PATH,
    params: { chatId, messageId },
    search: {},
  } as const;
}

/** Server-side / shareable path strings (no TanStack `$` placeholders). */
export function askPunttProjectReviewPathString(
  projectId: string,
  ticketId: string,
  commentId?: string,
): string {
  const base = `/ask-puntt/projects/${projectId}/reviews/${ticketId}`;
  return commentId ? `${base}/comments/${commentId}` : base;
}

export function askPunttProjectChatPathString(
  projectId: string,
  chatId: string,
): string {
  return `/ask-puntt/projects/${projectId}/chats/${chatId}`;
}

export function askPunttChatReviewPathString(
  chatId: string,
  ticketId: string,
  commentId?: string,
): string {
  const base = `/ask-puntt/${chatId}/reviews/${ticketId}`;
  return commentId ? `${base}/comments/${commentId}` : base;
}

export function parseAskPunttLocation(
  params: Record<string, string | undefined>,
  search?: Record<string, unknown>,
): AskPunttLocation {
  let version: number | undefined;
  if (search?.version != null && search.version !== "") {
    const parsed = Number(search.version);
    if (!Number.isNaN(parsed) && parsed >= 0) {
      version = parsed;
    }
  }

  return {
    projectId: params.projectId,
    chatId: params.chatId,
    ticketId: params.ticketId,
    commentId: params.commentId,
    canvasMessageId: params.messageId,
    version,
  };
}

/** Legacy `?review=` / `?chatId=` / `?comment=` → canonical path navigation. */
export function legacyCampaignSearchToNavigate(
  projectId: string,
  search: CampaignSearch & { comment?: string },
) {
  const comment =
    typeof search.comment === "string" && search.comment.trim()
      ? search.comment.trim()
      : undefined;

  if (search.review) {
    return buildProjectReviewPath(
      projectId,
      search.review,
      comment,
      search.version,
    );
  }
  if (search.chatId) {
    return buildProjectChatPath(projectId, search.chatId);
  }
  return null;
}

/** Legacy `?review=` / `?canvas=` / `?comment=` → canonical path navigation. */
export function legacyChatSearchToNavigate(
  chatId: string,
  search: ChatSearch & { comment?: string },
) {
  const comment =
    typeof search.comment === "string" && search.comment.trim()
      ? search.comment.trim()
      : undefined;

  if (search.review) {
    return buildChatReviewPath(chatId, search.review, comment, search.version);
  }
  if (search.canvas) {
    return buildChatCanvasPath(chatId, search.canvas);
  }
  return null;
}

/** Spread into `redirect()` — TanStack cannot infer unions from path builders. */
export function replacePathRedirect(location: {
  to: string;
  params: Record<string, string | undefined>;
  search?: object;
}) {
  const params = Object.fromEntries(
    Object.entries(location.params).filter(([, value]) => value != null),
  ) as Record<string, string>;
  const search =
    location.search != null && Object.keys(location.search).length > 0
      ? (location.search as Record<string, unknown>)
      : {};
  return {
    to: location.to,
    params,
    search,
    replace: true as const,
  };
}
